// Import Packages
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

// Import Pages
import { TextInput } from '../../../FlowConfigurationInputs/TextInput/NewTextInput';
import { Dropdown } from '../../../FlowConfigurationInputs';
import FieldLabel from '../../../FlowConfigurationInputs/FieldLabel/FieldLabel';
import { Checkboxes } from '../../../FlowConfigurationInputs';

// Import Material UI
import {
    Button, Dialog, DialogTitle, ButtonGroup, DialogContentText, DialogContent
} from "@material-ui/core/";
import Close from "@material-ui/icons/Close";
import CircularProgress from '@material-ui/core/CircularProgress';


// Import Recoil
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import { alertState, changedFields, initialActions, saveAvailable } from "../../../../recoil-store/recoil-state";

// Import Helpers
import { checkTheDifference, addDifference, removeDifference } from '../../../../helpers/helper';

// Import CSS
import "./table.css";

const ParamsModal = ({ action, inputName, title, setOpen, variables, setOpenVariable, actionType, customFields, index }) => {
    const { t } = useTranslation();
    const [refreshTime, setRefreshTime] = useState(new Date());
    const [columnSelector, setColumnSelector] = useState('name');
    const [copyAction, setCopyAction] = useState(undefined);

    const setAlert = useSetRecoilState(alertState);
    const initialActionsData = useRecoilValue(initialActions);
    const setSaveAvailable = useSetRecoilState(saveAvailable);
    const [changedFieldsState, setChangeFields] = useRecoilState(changedFields);

    const label = 'CONTENT.CONFIGURATION-FORM.DATA-TABLE.';

    useEffect(() => {
        if (actionType === 'create') {
            let ac = {
                column: "",
                columnSelector: "name"
            }
            setCopyAction(ac);
            setColumnSelector('name');
        } else {
            let ac = {
                column: { value: action.fields[inputName].value[index].column },
                columnSelector: action.fields[inputName].value[index].columnSelector
            }
            if (customFields) {
                for (let field of customFields) {
                    if (action.fields[inputName].value[index][field.inputName]) {
                        ac[field.inputName] = { value: action.fields[inputName].value[index][field.inputName] }
                    }

                }
            }

            setCopyAction(ac);
            setColumnSelector(action.fields[inputName].value[index].columnSelector);
        }
    }, [])

    const changeColumnSelector = (selector) => {

        setCopyAction(prevState => ({
            ...prevState,
            columnSelector: selector,
        }));
        setColumnSelector(selector);
    };

    const handleClose = () => {
        setCopyAction(undefined);
        setOpen({ show: false, index: -1 });
    };

    const checkDiff = () => {
        // the difference between the old value and the new value is checked
        const field = initialActionsData[action.id]?.fields[inputName];
        const oldValue = !field ? undefined : field.value;
        const cfTemp = JSON.parse(JSON.stringify(changedFieldsState));
        if (!oldValue) {
            addDifference(cfTemp, action, inputName, setChangeFields, setSaveAvailable)
        } else {
            const arr1 = action.fields[inputName].value;

            if (checkTheDifference(arr1, oldValue, Object.keys(copyAction))) {
                addDifference(cfTemp, action, inputName, setChangeFields, setSaveAvailable)
            } else {
                removeDifference(cfTemp, action, inputName, setChangeFields, setSaveAvailable)
            }
        }
    }

    const getFieldsValues = () => {
        let data = {};
        for (let key of Object.keys(copyAction)) {
            let v = copyAction[key].value || copyAction[key].value === '' || copyAction[key].value === false ? copyAction[key].value : copyAction[key];
            if (!v && (copyAction[key]?.isRequired)) {

                if (customFields) {
                    const field = customFields.find(x => x.inputName === key);
                    if (field && field.depends) {
                        let d = field.depends.split('.')
                        if (!(copyAction[d[0]] && (copyAction[d[0]] === d[1] || copyAction[d[0]].value === d[1]))) {
                            continue
                        }
                    }
                }
                setAlert(alert => ({ ...alert, show: true, message: t("GENERAL.NOT-NULL"), severity: 'error' }))
                return;
            }
            data[key] = v;
        }
        return data;
    }

    const handleAdd = () => {
        let data = getFieldsValues();
        if (!data) {
            return;
        }
        action.fields[inputName].value.push(data); // { column: copyAction.column?.value, columnSelector: copyAction.columnSelector }

        checkDiff();

        setOpen({ show: false, index: -1 });
    };

    const handleEdit = () => {

        let data = getFieldsValues();
        if (!data) {
            return;
        }
        action.fields[inputName].value[index] = data; //{ column: copyAction.column?.value, columnSelector: copyAction.columnSelector };

        checkDiff();
        setOpen({ show: false, index: -1 });
    };

    const handleDelete = () => {
        action.fields[inputName].value.splice(index, 1);
        checkDiff();

        setOpen({ show: false, index: -1 });
    };

    const showElementByType = (field) => {

        if (field.depends) {
            let d = field.depends.split('.')
            if (!(copyAction[d[0]] && (copyAction[d[0]] === d[1] || copyAction[d[0]].value === d[1]))) {
                return undefined;
            }
        }
        switch (field.type) {
            case "select":
                return <div className="conf-field-container">
                    <FieldLabel name={field.title} />
                    <div style={{ display: "flex", justifyContent: "space-between", width: "90%" }}>
                        <Dropdown
                            action={copyAction}
                            inputName={field.inputName}
                            options={field.options}
                            setField={() => setRefreshTime(new Date())}
                        />
                    </div>
                </div>
            case "string":
                return <div className="conf-field-container">
                    <FieldLabel name={field.title} is_required={field.is_required} />
                    <TextInput
                        action={copyAction}
                        inputName={field.inputName}
                        variables={variables}
                        setOpenVariable={setOpenVariable}
                        isRequired={field.is_required}
                        hideRequired
                    />
                    {field.eg && <small className="conf-field-desc"><em>{field.eg}</em></small>}
                </div>
            case "checkbox":
                return <div className="conf-field-container">
                    <Checkboxes action={copyAction} inputName={field.inputName} options={{ label: field.title }} />
                </div>
            default:
                break;
        }
    }

    return (
        <Dialog
            open={true}
            onClose={handleClose}
            data-test="about-dialog"
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="variables-dialog"

        >
            {
                !copyAction ? <CircularProgress className='loading-circular-process' /> :
                    <div>
                        <DialogTitle id="alert-dialog-title">
                            <div id="about-title">
                                {t(actionType === 'create' ? "PARAMS-MODAL.ADD-NEW" : "PARAMS-MODAL.EDIT")} {title}
                                <Button data-test="canceldialog-button" onClick={handleClose}>
                                    <Close />
                                </Button>
                            </div>
                        </DialogTitle>
                        <DialogContent
                            id="alert-dialog-description"
                            className="dialog-content-area"
                            style={{ width: "400px" }}
                        >
                            <DialogContentText style={{ color: "black" }}>
                                <ButtonGroup size="medium" aria-label="small outlined button group">
                                    <Button
                                        className={columnSelector === 'index' ? "btn-gray" : ""}
                                        id="button-group"
                                        onClick={(e) => changeColumnSelector('index')}
                                        variant="outlined"
                                    >
                                        {t(label + "COLUMN-INDEX")}
                                    </Button>
                                    <Button
                                        className={columnSelector === 'name' ? "btn-gray" : ""}
                                        id="button-group"
                                        onClick={(e) => changeColumnSelector('name')}
                                        variant="outlined"
                                    >
                                        {t(label + "COLUMN-NAME")}
                                    </Button>
                                </ButtonGroup>
                                <div className="conf-field-container m-t-20">
                                    <FieldLabel name={t(label + 'COLUMN')} is_required />
                                    <TextInput
                                        action={copyAction}
                                        inputName={"column"}
                                        variables={variables}
                                        setOpenVariable={setOpenVariable}
                                        isRequired
                                        hideRequired
                                    />
                                    <small className="conf-field-desc"><em>{t(label + 'COLUMN-INFO')}</em></small>
                                </div>
                            </DialogContentText>

                            {
                                refreshTime && customFields && customFields.map((field) => showElementByType(field))
                            }

                            <Button variant="outlined" data-test="submitdialog-button"
                                style={{ float: "right", backgroundColor: "#46474E", borderRadius: "2px", color: "#fff", width: "45%", border: "none" }}
                                onClick={() => actionType === 'create' ? handleAdd() : handleEdit()}>
                                {actionType === 'create' ? t("PARAMS-MODAL.ADD") : t("PARAMS-MODAL.EDIT")}
                            </Button>
                            <Button variant="outlined" data-test="submitdialog-button"
                                style={{ float: "left", backgroundColor: "#D75E5E", borderRadius: "2px", color: "#fff", width: "45%", border: "none" }}
                                onClick={() => actionType === 'create' ? handleClose() : handleDelete()}>
                                {actionType === 'create' ? t("PARAMS-MODAL.CANCEL") : t("PARAMS-MODAL.DELETE")}
                            </Button>
                        </DialogContent>
                    </div>

            }

        </Dialog>
    );
};

export default ParamsModal;
